<template>
  <div class="user-management-page">
    <div class="page-header">
      <div>
        <h1>User Management</h1>
        <p class="page-subtitle" v-if="isSuperAdmin">
          Kelola semua user di semua grup. Anda dapat menambah, mengedit, dan menghapus user.
        </p>
        <p class="page-subtitle" v-else>
          Kelola user di grup Anda. Anda dapat menambah, mengedit, dan menghapus user.
        </p>
      </div>
    </div>

    <div class="content-container">
      <!-- Search and Add Button -->
      <div class="filters-section">
        <a-input
          v-model:value="filters.name"
          placeholder="Cari nama user"
          size="large"
          @change="handleSearch"
          class="filter-input mr-3"
          style="flex: 1"
        >
          <template #prefix>
            <search-outlined />
          </template>
        </a-input>
        <a-button
          type="primary"
          size="large"
          danger
          @click="showCreateModal"
          style="border-radius: 50px; font-weight: 700"
        >
          <plus-outlined /> TAMBAH
        </a-button>
      </div>

      <!-- Loading -->
      <div v-if="loading" class="loading-container">
        <a-spin size="large" />
      </div>

      <!-- Users Table -->
      <a-table
        :columns="tableColumns"
        :data-source="users"
        :loading="loading"
        :pagination="false"
        :scroll="{ x: 1200 }"
        row-key="id"
        @change="handleTableChange"
      >
        <template #bodyCell="{ column, record }">
          <!-- User Column -->
          <template v-if="column.key === 'name'">
            <div class="user-cell">
              <div class="user-name">{{ record.name }}</div>
              <div class="user-email">{{ record.email }}</div>
            </div>
          </template>

          <!-- Role Column -->
          <template v-else-if="column.key === 'role'">
            <a-tag :color="getRoleColor(record.role)">
              {{ getRoleLabel(record.role) }}
            </a-tag>
          </template>
        </template>

        <!-- Actions Column with dedicated slot -->
        <template #actions="{ record }">
          <div class="action-buttons">
            <a-button
              type="primary"
              danger
              shape="circle"
              size="large"
              @click="showEditModal(record)"
            >
              <edit-outlined />
            </a-button>
            <a-button
              type="default"
              shape="circle"
              size="large"
              class="btn-delete"
              @click="showDeleteModal(record)"
            >
              <delete-outlined />
            </a-button>
          </div>
        </template>
      </a-table>

      <!-- Pagination -->
      <div v-if="totalData > 0" class="pagination-container">
        <a-pagination
          v-model:current="pagination.page"
          v-model:page-size="pagination.pageSize"
          :total="totalData"
          :show-total="total => `Total ${total} users`"
          :show-size-changer="true"
          @change="handlePageChange"
          @showSizeChange="handleSizeChange"
        />
      </div>
    </div>

    <!-- Create/Edit Modal -->
    <a-modal
      v-model:visible="modalVisible"
      :title="isEditMode ? 'Edit User' : 'Add New User'"
      width="700px"
      @ok="handleSubmit"
      @cancel="handleCancel"
      :confirm-loading="submitting"
    >
      <a-form :model="formData" layout="vertical" ref="formRef">
        <a-row :gutter="16">
          <a-col :span="12">
            <a-form-item
              label="Full Name"
              name="name"
              :rules="[{ required: true, message: 'Please enter name' }]"
            >
              <a-input v-model:value="formData.name" placeholder="Enter full name" />
            </a-form-item>
          </a-col>
          <a-col :span="12">
            <a-form-item
              label="Email"
              name="email"
              :rules="[
                { required: true, message: 'Please enter email' },
                { type: 'email', message: 'Invalid email format' },
              ]"
            >
              <a-input v-model:value="formData.email" placeholder="Enter email" />
            </a-form-item>
          </a-col>
          <a-col :span="12">
            <a-form-item
              label="Gender"
              name="gender"
              :rules="[{ required: true, message: 'Please select gender' }]"
            >
              <a-select v-model:value="formData.gender" placeholder="Select gender">
                <a-select-option value="L">Male</a-select-option>
                <a-select-option value="P">Female</a-select-option>
              </a-select>
            </a-form-item>
          </a-col>
        </a-row>

        <a-row :gutter="16">
          <a-col :span="12">
            <a-form-item
              label="Role"
              name="role"
              :rules="[{ required: true, message: 'Please select role' }]"
            >
              <a-select
                v-model:value="formData.role"
                placeholder="Select role"
                :disabled="!isSuperAdmin && isEditMode"
              >
                <a-select-option value="admin" v-if="isSuperAdmin">Admin</a-select-option>
                <a-select-option value="ketua">Ketua</a-select-option>
                <a-select-option value="sekretaris">Sekretaris</a-select-option>
                <a-select-option value="bendahara">Bendahara</a-select-option>
                <a-select-option value="pengurus">Pengurus</a-select-option>
                <a-select-option value="pekerja_harian">Pekerja Harian</a-select-option>
              </a-select>
            </a-form-item>
          </a-col>
          <a-col :span="12" v-if="isSuperAdmin">
            <a-form-item label="Group" name="groupId">
              <a-select
                v-model:value="formData.groupId"
                placeholder="Select group"
                show-search
                :filter-option="filterGroupOption"
                allow-clear
              >
                <a-select-option v-for="group in groups" :key="group.id" :value="group.id">
                  {{ group.name }}
                </a-select-option>
              </a-select>
            </a-form-item>
          </a-col>
        </a-row>

        <a-row :gutter="16">
          <a-col :span="12">
            <a-form-item label="NPA (Nomor Pokok Anggota)" name="npa">
              <a-input v-model:value="formData.npa" placeholder="Enter NPA" />
            </a-form-item>
          </a-col>
          <a-col :span="12">
            <a-form-item label="Jabatan" name="jabatan">
              <a-input v-model:value="formData.jabatan" placeholder="Enter position" />
            </a-form-item>
          </a-col>
        </a-row>

        <a-row :gutter="16">
          <a-col :span="12">
            <a-form-item label="Phone" name="telepon">
              <a-input v-model:value="formData.telepon" placeholder="Enter phone number" />
            </a-form-item>
          </a-col>
          <a-col :span="12" v-if="!isEditMode">
            <a-form-item label="Password" name="password">
              <a-input-password
                v-model:value="formData.password"
                placeholder="Leave empty for default (password123)"
              />
            </a-form-item>
          </a-col>
        </a-row>
      </a-form>
    </a-modal>

    <!-- Delete Confirmation Modal -->
    <a-modal
      v-model:visible="deleteModalVisible"
      :closable="true"
      width="500px"
      :footer="null"
      centered
    >
      <div class="modal-confirm">
        <h3 class="modal-title">
          Tindakan ini akan menghapus akun user. Apakah Anda ingin melanjutkan?
        </h3>
        <p class="modal-description">Akun yang dihapus tidak dapat dikembalikan.</p>
        <div class="modal-actions">
          <a-button class="btn-cancel" @click="cancelDelete" :disabled="deleting">
            KEMBALI
          </a-button>
          <a-button class="btn-confirm" @click="handleDeleteConfirm" :loading="deleting">
            YA, HAPUS
          </a-button>
        </div>
      </div>
    </a-modal>
  </div>
</template>

<script>
import { ref, reactive, computed, onMounted, watch, nextTick } from 'vue'
import { useRouter } from 'vue-router'
import { notification } from 'ant-design-vue'
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  SearchOutlined,
  MailOutlined,
} from '@ant-design/icons-vue'
import { userAPI, groupAPI } from '@/services/axios/perki'

export default {
  name: 'UserManagement',
  components: {
    PlusOutlined,
    EditOutlined,
    DeleteOutlined,
    SearchOutlined,
    // MailOutlined,
  },
  setup() {
    const router = useRouter()
    const loading = ref(false)
    const submitting = ref(false)
    const users = ref([])
    const groups = ref([])
    const totalData = ref(0)
    const modalVisible = ref(false)
    const isEditMode = ref(false)
    const editingUserId = ref(null)
    const formRef = ref(null)
    const deleteModalVisible = ref(false)
    const deletingUser = ref(null)
    const deleting = ref(false)

    const currentUser = JSON.parse(localStorage.getItem('perki_user') || '{}')
    const userRole = localStorage.getItem('perki_role') || currentUser.role
    const userGroupId = localStorage.getItem('perki_group_id') || currentUser.groupId

    const isSuperAdmin = computed(() => userRole === 'super_admin')

    const filters = reactive({
      name: '',
      email: '',
      role: [],
      groupId: [],
    })

    const sorter = reactive({
      field: '',
      order: '',
    })

    const pagination = reactive({
      page: 1,
      pageSize: 10,
    })

    const formData = reactive({
      name: '',
      email: '',
      gender: '',
      role: '',
      groupId: '',
      npa: '',
      jabatan: '',
      telepon: '',
      password: '',
    })

    const columns = [
      {
        title: 'User',
        dataIndex: 'name',
        key: 'name',
        width: 250,
        fixed: 'left',
        slots: { customRender: 'name' },
      },
      {
        title: 'Role',
        dataIndex: 'role',
        key: 'role',
        width: 120,
        slots: { customRender: 'role' },
      },
      {
        title: 'Group',
        dataIndex: 'group.name',
        key: 'group',
        width: 180,
      },
      {
        title: 'NPA',
        dataIndex: 'npa',
        key: 'npa',
        width: 120,
      },
      {
        title: 'Jabatan',
        dataIndex: 'jabatan',
        key: 'jabatan',
        width: 180,
      },
      {
        title: 'Phone',
        dataIndex: 'telepon',
        key: 'telepon',
        width: 140,
      },
      {
        title: 'Actions',
        key: 'actions',
        width: 180,
        fixed: 'right',
        slots: { customRender: 'actions' },
      },
    ]

    const tableColumns = computed(() => {
      const cols = [
        {
          title: 'User',
          dataIndex: 'name',
          key: 'name',
          width: 280,
          fixed: 'left',
          sorter: true,
          sortOrder: sorter.field === 'name' ? sorter.order : null,
        },
        {
          title: 'Email',
          dataIndex: 'email',
          key: 'email',
          width: 220,
          sorter: true,
          sortOrder: sorter.field === 'email' ? sorter.order : null,
        },
        {
          title: 'Role',
          dataIndex: 'role',
          key: 'role',
          width: 150,
          filters: [
            { text: 'Super Admin', value: 'super_admin' },
            { text: 'Admin', value: 'admin' },
            { text: 'Ketua', value: 'ketua' },
            { text: 'Sekretaris', value: 'sekretaris' },
            { text: 'Bendahara', value: 'bendahara' },
            { text: 'Pengurus', value: 'pengurus' },
            { text: 'Pekerja Harian', value: 'pekerja_harian' },
          ],
          filteredValue: filters.role,
          customRender: ({ record }) => getRoleLabel(record.role),
        },
        {
          title: 'NPA',
          dataIndex: 'npa',
          key: 'npa',
          width: 130,
        },
        {
          title: 'Jabatan',
          dataIndex: 'jabatan',
          key: 'jabatan',
          width: 180,
        },
        {
          title: 'Phone',
          dataIndex: 'telepon',
          key: 'telepon',
          width: 150,
        },
        {
          title: 'Actions',
          key: 'actions',
          width: 120,
          fixed: 'right',
          slots: { customRender: 'actions' },
        },
      ]

      // Add Group column for super admin
      if (isSuperAdmin.value) {
        cols.splice(4, 0, {
          title: 'Group',
          key: 'group',
          width: 200,
          filters: groups.value.map(g => ({ text: g.name, value: g.id })),
          filteredValue: filters.groupId,
          customRender: ({ record }) => record.group?.name || '-',
        })
      }

      return cols
    })

    const checkAuth = () => {
      const token = localStorage.getItem('perki_token')
      if (!token) {
        notification.error({
          message: 'Authentication Required',
          description: 'Please login to access this page',
        })
        router.push('/masuk')
        return false
      }
      if (userRole !== 'super_admin' && userRole !== 'admin') {
        notification.error({
          message: 'Access Denied',
          description: 'Admin only.',
        })
        router.push('/')
        return false
      }
      return true
    }

    const fetchGroups = async () => {
      if (!isSuperAdmin.value) return

      try {
        const response = await groupAPI.getAll({ page: 1, limit: 100, isActive: true })
        groups.value = response.data.data.groups || []
      } catch (error) {
        console.error('Error fetching groups:', error)
      }
    }

    const fetchUsers = async () => {
      if (!checkAuth()) return

      loading.value = true
      try {
        const params = {
          page: pagination.page,
          pageSize: pagination.pageSize,
        }

        if (filters.name) params.name = filters.name
        if (filters.email) params.email = filters.email

        // Handle role filter
        if (filters.role && filters.role.length > 0) {
          params.role = filters.role.join(',')
        }

        // Handle group filter
        if (filters.groupId && filters.groupId.length > 0) {
          params.groupId = filters.groupId.join(',')
        }

        // Handle sorting
        if (sorter.field && sorter.order) {
          params.sortBy = sorter.field
          params.sortOrder = sorter.order === 'ascend' ? 'asc' : 'desc'
        }

        const response = await userAPI.getAll(params)
        const result = response.data.data
        users.value = result.users || []
        console.log(users.value, 'cekuser')
        totalData.value = result.pagination?.totalData || 0
      } catch (error) {
        console.error('Error fetching users:', error)
        notification.error({
          message: 'Error',
          description: 'Failed to load users',
        })
      } finally {
        loading.value = false
      }
    }

    const handleSearch = () => {
      pagination.page = 1
      fetchUsers()
    }

    const handleTableChange = (pag, tableFilters, tableSorter) => {
      console.log('Table change:', { pag, tableFilters, tableSorter })

      // Update filters
      if (tableFilters.role !== undefined) {
        filters.role = tableFilters.role || []
      }
      if (tableFilters.group !== undefined) {
        filters.groupId = tableFilters.group || []
      }

      // Update sorter
      if (tableSorter.field) {
        sorter.field = tableSorter.field
        sorter.order = tableSorter.order || ''
      } else {
        sorter.field = ''
        sorter.order = ''
      }

      // Reset to page 1 when filtering/sorting
      pagination.page = 1
      fetchUsers()
    }

    const handlePageChange = (page, pageSize) => {
      pagination.page = page
      pagination.pageSize = pageSize
      fetchUsers()
    }

    const handleSizeChange = (current, size) => {
      pagination.page = 1
      pagination.pageSize = size
      fetchUsers()
    }

    const showCreateModal = () => {
      isEditMode.value = false
      editingUserId.value = null

      // Reset form data immediately
      Object.assign(formData, {
        name: '',
        email: '',
        gender: '',
        role: '',
        groupId: '',
        npa: '',
        jabatan: '',
        telepon: '',
        password: '',
      })

      // Clear form validation after modal opens
      nextTick(() => {
        if (formRef.value) {
          formRef.value.clearValidate()
        }
      })

      modalVisible.value = true
    }

    const showEditModal = record => {
      isEditMode.value = true
      editingUserId.value = record.id
      formData.name = record.name
      formData.email = record.email
      formData.gender = record.gender
      formData.role = record.role
      formData.groupId = record.groupId || ''
      formData.npa = record.npa || ''
      formData.jabatan = record.jabatan || ''
      formData.telepon = record.telepon || ''
      modalVisible.value = true
    }

    const handleSubmit = async () => {
      try {
        await formRef.value.validate()
        submitting.value = true

        const payload = {
          name: formData.name,
          email: formData.email,
          gender: formData.gender,
          role: formData.role,
          npa: formData.npa || undefined,
          jabatan: formData.jabatan || undefined,
          telepon: formData.telepon || undefined,
        }

        // Super admin can set groupId, admin cannot
        if (isSuperAdmin.value && formData.groupId) {
          payload.groupId = formData.groupId
        }

        // Password only for create
        if (!isEditMode.value && formData.password) {
          payload.password = formData.password
        }

        if (isEditMode.value) {
          await userAPI.update(editingUserId.value, payload)
          notification.success({
            message: 'Success',
            description: 'User updated successfully',
          })
        } else {
          await userAPI.create(payload)
          notification.success({
            message: 'Success',
            description: 'User created successfully',
          })
        }

        modalVisible.value = false
        fetchUsers()
      } catch (error) {
        console.error('Error saving user:', error)
        const errorMsg = error.response?.data?.message || 'Failed to save user'

        // Handle specific error for duplicate NIK
        if (errorMsg.includes('duplicate key') && errorMsg.includes('uni_users_nik')) {
          notification.error({
            message: 'Duplicate NIK',
            description: 'NIK sudah terdaftar. Silakan gunakan NIK yang berbeda.',
          })
        } else if (errorMsg.includes('duplicate') && errorMsg.includes('nik')) {
          notification.error({
            message: 'Duplicate NIK',
            description: 'NIK sudah terdaftar. Silakan gunakan NIK yang berbeda.',
          })
        } else if (errorMsg.includes('duplicate key') && errorMsg.includes('email')) {
          notification.error({
            message: 'Duplicate Email',
            description: 'Email sudah terdaftar. Silakan gunakan email yang berbeda.',
          })
        } else if (errorMsg.includes('duplicate') && errorMsg.includes('email')) {
          notification.error({
            message: 'Duplicate Email',
            description: 'Email sudah terdaftar. Silakan gunakan email yang berbeda.',
          })
        } else {
          notification.error({
            message: 'Error',
            description: errorMsg,
          })
        }
      } finally {
        submitting.value = false
      }
    }

    const showDeleteModal = record => {
      deletingUser.value = record
      deleteModalVisible.value = true
    }

    const cancelDelete = () => {
      deleteModalVisible.value = false
      deletingUser.value = null
    }

    const handleDeleteConfirm = async () => {
      if (!deletingUser.value) return

      deleting.value = true
      try {
        await userAPI.delete(deletingUser.value.id)
        notification.success({
          message: 'Success',
          description: 'User deleted successfully',
        })
        deleteModalVisible.value = false
        fetchUsers()
      } catch (error) {
        console.error('Error deleting user:', error)
        const errorMsg = error.response?.data?.message || 'Failed to delete user'
        notification.error({
          message: 'Error',
          description: errorMsg,
        })
      } finally {
        deleting.value = false
        deletingUser.value = null
      }
    }

    const handleDelete = async id => {
      try {
        await userAPI.delete(id)
        message.success('User deleted successfully')
        fetchUsers()
      } catch (error) {
        console.error('Error deleting user:', error)
        const errorMsg = error.response?.data?.message || 'Failed to delete user'
        message.error(errorMsg)
      }
    }

    const handleCancel = () => {
      modalVisible.value = false
      // Reset form after modal closes
      nextTick(() => {
        resetForm()
      })
    }

    const resetForm = () => {
      editingUserId.value = null
      Object.assign(formData, {
        name: '',
        email: '',
        gender: '',
        role: '',
        groupId: '',
        npa: '',
        jabatan: '',
        telepon: '',
        password: '',
      })
      if (formRef.value) {
        formRef.value.clearValidate()
      }
    }

    const getRoleColor = role => {
      const colors = {
        super_admin: 'purple',
        admin: 'blue',
        ketua: 'green',
        sekretaris: 'cyan',
        bendahara: 'orange',
        pengurus: 'default',
        pekerja_harian: 'gray',
      }
      return colors[role] || 'default'
    }

    const getRoleLabel = role => {
      const labels = {
        super_admin: 'Super Admin',
        admin: 'Admin',
        ketua: 'Ketua',
        sekretaris: 'Sekretaris',
        bendahara: 'Bendahara',
        pengurus: 'Pengurus',
        pekerja_harian: 'Pekerja Harian',
      }
      return labels[role] || role
    }

    const filterGroupOption = (input, option) => {
      return option.children[0].children.toLowerCase().indexOf(input.toLowerCase()) >= 0
    }

    onMounted(() => {
      fetchGroups()
      fetchUsers()
    })

    return {
      loading,
      submitting,
      users,
      groups,
      totalData,
      filters,
      sorter,
      pagination,
      columns,
      tableColumns,
      modalVisible,
      isEditMode,
      formData,
      formRef,
      isSuperAdmin,
      fetchUsers,
      handleSearch,
      handleTableChange,
      handlePageChange,
      handleSizeChange,
      showCreateModal,
      showEditModal,
      handleSubmit,
      handleDelete,
      handleCancel,
      getRoleColor,
      getRoleLabel,
      filterGroupOption,
      deleteModalVisible,
      deleting,
      showDeleteModal,
      cancelDelete,
      handleDeleteConfirm,
    }
  },
}
</script>

<style lang="scss" scoped>
.user-management-page {
  padding: 24px;
  min-height: 100vh;

  .page-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 24px;

    h1 {
      margin: 0 0 8px 0;
      font-size: 28px;
      font-weight: 600;
      color: #333;
    }

    .page-subtitle {
      margin: 0;
      color: #666;
      font-size: 14px;
      max-width: 600px;
      line-height: 1.5;
    }
  }

  .filters-section {
    margin-bottom: 24px;
    display: flex;

    :deep(.ant-input-affix-wrapper) {
      border-radius: 24px;
      padding: 8px 20px;
    }
  }

  .content-container {
    background: white;
    border-radius: 8px;
    padding: 24px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  }

  // Table Styles
  :deep(.ant-table) {
    // Header background
    .ant-table-thead > tr > th {
      background-color: #f2f4f8;
      color: #1f2937;
      font-weight: 600;
    }

    // Body background
    .ant-table-tbody > tr > td {
      background-color: #ffffff;
    }

    .user-cell {
      .user-name {
        font-size: 14px;
        font-weight: 600;
        color: #1f2937;
        margin-bottom: 4px;
      }

      .user-email {
        font-size: 12px;
        color: #6b7280;
      }
    }

    .action-buttons {
      display: flex;
      gap: 8px;
      justify-content: center;

      .btn-delete {
        background-color: #333;
        border-color: #333;
        color: white;

        &:hover {
          background-color: #555;
          border-color: #555;
        }
      }
    }
  }

  .pagination-container {
    margin-top: 24px;
    display: flex;
    justify-content: flex-end;
  }
}

:deep(.ant-input::placeholder),
:deep(.ant-input-number::placeholder),
:deep(.ant-picker::placeholder),
:deep(.ant-select-selector::placeholder),
:deep(.ant-select-selection-placeholder),
:deep(.ant-textarea::placeholder) {
  color: #bfbfbf !important;
  opacity: 1 !important;
  font-size: 14px;
}

:deep(.ant-select-selection-item) {
  font-size: 14px !important;
}

:deep(.ant-input-placeholder),
:deep(.ant-textarea-placeholder) {
  color: #bfbfbf !important;
}

// Modal Confirm Styles
.modal-confirm {
  padding: 8px 0;

  .modal-title {
    font-size: 18px;
    font-weight: 600;
    color: #1a1a1a;
    margin: 0 0 12px 0;
  }

  .modal-description {
    font-size: 14px;
    color: #595959;
    line-height: 1.6;
    margin: 0 0 24px 0;
  }

  .modal-actions {
    display: flex;
    gap: 12px;
    justify-content: flex-end;

    .btn-cancel {
      border: 2px solid #1a1a1a;
      color: #1a1a1a;
      background: white;
      border-radius: 50px;
      font-weight: 600;
      height: 40px;
      padding: 0 24px;
      font-size: 14px;
      width: 100%;

      &:hover:not(:disabled) {
        background: #1a1a1a;
        color: white;
      }

      &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
      }
    }

    .btn-confirm {
      background: #e10f28;
      border: 2px solid #e10f28;
      color: white;
      border-radius: 50px;
      font-weight: 600;
      height: 40px;
      padding: 0 24px;
      font-size: 14px;
      width: 100%;

      &:hover:not(:disabled) {
        background: #c20d23;
        border-color: #c20d23;
      }

      &:disabled {
        background: #98a2b3;
        border-color: #98a2b3;
        cursor: not-allowed;
      }
    }
  }
}

@media (max-width: 768px) {
  .user-management-page {
    padding: 16px;

    .filters-section {
      flex-direction: column;

      .filter-input {
        width: 100%;
      }
    }

    .content-container {
      padding: 16px;
    }
  }
}
</style>
